import React, { useState } from "react";
import { Banknote, CreditCard, ChevronDown, Ticket, Landmark, PenLine, Layers, Gift, FileClock, X, Trash2 } from "lucide-react";
import { formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { PAYMENT_METHODS, paymentMethodLabel } from "@/lib/fiscal/paymentMethods";
import { authorizeCardIfNeeded } from "@/lib/paymentPos";

const METHOD_ICONS = { cash: Banknote, card: CreditCard, assegno: PenLine, ticket: Ticket, non_riscosso: FileClock, omaggio: Gift, bonifico: Landmark };

// Modale pagamento multiplo: divide il totale su più metodi, mostra il rimanente
// e si chiude solo quando il totale è completamente coperto.
function MultiPayModal({ total, onClose, onConfirm, testidPrefix }) {
  const [rows, setRows] = useState([]);
  const [input, setInput] = useState("");
  const covered = rows.reduce((s, r) => s + r.amount, 0);
  const remaining = Math.max(0, Math.round((total - covered) * 100) / 100);
  const cashGiven = (Array.isArray(rows) ? rows : []).filter((r) => r.method === "cash").reduce((s, r) => s + (r.given || r.amount), 0);
  const cashApplied = (Array.isArray(rows) ? rows : []).filter((r) => r.method === "cash").reduce((s, r) => s + r.amount, 0);
  const change = Math.round((cashGiven - cashApplied) * 100) / 100;

  const addPay = (method) => {
    if (remaining <= 0.004) return;
    let amt = parseFloat(input) || remaining;
    if (amt <= 0) { toast.error("Importo non valido"); return; }
    let given = null;
    if (method === "cash" && amt > remaining) { given = amt; amt = remaining; }
    else if (amt > remaining) amt = remaining;
    setRows((prev) => [...prev, { method, amount: Math.round(amt * 100) / 100, given }]);
    setInput("");
  };
  const removeRow = (i) => setRows((prev) => (Array.isArray(prev) ? prev : []).filter((_, idx) => idx !== i));

  const confirm = () => {
    if (remaining > 0.004) { toast.error(`Mancano ancora ${formatEUR(remaining)}`); return; }
    const breakdown = {};
    rows.forEach((r) => { breakdown[r.method] = Math.round(((breakdown[r.method] || 0) + r.amount) * 100) / 100; });
    const methods = Object.keys(breakdown);
    const method = methods.length === 1 ? methods[0] : "mixed";
    const opts = {};
    if (change > 0.004) opts.cash_tendered = Math.round(cashGiven * 100) / 100;
    onConfirm(method, methods.length === 1 ? null : breakdown, opts);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-md w-full max-w-lg p-5" data-testid={`${testidPrefix}-multi-modal`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-[#1E2D5B]" /> Pagamento multiplo</h3>
          <button data-testid={`${testidPrefix}-multi-close`} onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className={`rounded-md px-4 py-3 mb-3 flex items-center justify-between ${remaining > 0.004 ? "bg-velta-bg border border-velta-border" : "bg-[#15803D] text-white"}`}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Totale {formatEUR(total)}</div>
            <div className="text-xs font-mono uppercase tracking-widest font-bold">
              {remaining > 0.004 ? "Rimanente da coprire" : change > 0.004 ? `Coperto ✓ — Resto ${formatEUR(change)}` : "Coperto ✓"}
            </div>
          </div>
          <div data-testid={`${testidPrefix}-multi-remaining`} className="font-mono font-bold text-3xl tabular-nums">
            {formatEUR(remaining)}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-velta-mute shrink-0">Importo</span>
          <input
            data-testid={`${testidPrefix}-multi-input`}
            type="number"
            min="0"
            step="0.01"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={remaining > 0 ? formatEUR(remaining) : "0,00"}
            className="h-11 flex-1 px-3 border border-velta-border rounded font-mono text-right focus:border-velta-ink outline-none"
          />
          <span className="text-[10px] text-velta-mute shrink-0">vuoto = tutto il rimanente</span>
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {(Array.isArray(PAYMENT_METHODS) ? PAYMENT_METHODS : []).map(({ id, label }) => {
            const Icon = METHOD_ICONS[id];
            return (
              <button
                key={id}
                data-testid={`${testidPrefix}-multi-add-${id}`}
                onClick={() => addPay(id)}
                disabled={remaining <= 0.004}
                className="h-14 rounded-md border border-velta-border hover:border-velta-ink bg-white text-xs font-medium flex flex-col items-center justify-center gap-1 btn-tactile disabled:opacity-30"
              >
                <Icon className="w-4 h-4 text-velta-mute" />
                <span className="leading-tight text-center px-1">{label}</span>
              </button>
            );
          })}
        </div>

        {rows.length > 0 && (
          <div className="border border-velta-border rounded-md divide-y divide-velta-border mb-3 max-h-40 overflow-y-auto">
            {(Array.isArray(rows) ? rows : []).map((r, i) => (
              <div key={i} className="flex items-center px-3 py-2 gap-2 text-sm" data-testid={`${testidPrefix}-multi-row-${i}`}>
                <span className="flex-1 font-medium">
                  {paymentMethodLabel(r.method)}
                  {r.given && <span className="ml-1 text-[10px] font-mono text-velta-mute">(dato {formatEUR(r.given)} → resto {formatEUR(r.given - r.amount)})</span>}
                </span>
                <span className="font-mono font-bold">{formatEUR(r.amount)}</span>
                <button data-testid={`${testidPrefix}-multi-remove-${i}`} onClick={() => removeRow(i)} className="text-velta-mute hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="h-12 rounded-md border border-velta-border text-sm font-medium">Annulla</button>
          <button
            data-testid={`${testidPrefix}-multi-confirm`}
            onClick={confirm}
            disabled={remaining > 0.004 || rows.length === 0}
            className="h-12 rounded-md bg-velta-ink text-white text-sm font-semibold btn-tactile disabled:opacity-40"
          >
            Emetti scontrino
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Blocco pagamenti checkout:
 * - CONTANTI (verde) e CARTA (blu): 1 tocco → scontrino immediato
 * - "Altri pagamenti": Pagamento multiplo + metodi RT (assegno, ticket, non riscosso, omaggio, bonifico) + metodi custom
 * - children: slot per il bottone Preconto accanto alla tendina
 */
export default function CheckoutPayments({ total, disabled, onPay, customMethods = [], testidPrefix = "neg", children }) {
  const [open, setOpen] = useState(false);
  const [showMulti, setShowMulti] = useState(false);
  const [busy, setBusy] = useState(false);

  const pay = async (method, breakdown, opts) => {
    if (busy) return;
    setOpen(false);
    setBusy(true);
    try {
      const pos = await authorizeCardIfNeeded({ method, breakdown, amount: total });
      if (!pos.ok) {
        toast.error(pos.message || "Pagamento carta non autorizzato");
        return;
      }
      if (pos.mode === "pos") toast.success("Carta autorizzata sul POS");
      onPay(method, breakdown, opts);
    } finally {
      setBusy(false);
    }
  };

  const singles = (Array.isArray(PAYMENT_METHODS) ? PAYMENT_METHODS : []).filter((m) => m.id !== "cash" && m.id !== "card");

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid={`${testidPrefix}-pay-cash-big`}
          disabled={disabled || busy}
          onClick={() => pay("cash")}
          className="h-24 rounded-lg bg-[#15803D] hover:bg-[#166534] text-white flex flex-col items-center justify-center gap-1.5 shadow-md btn-tactile disabled:opacity-40"
        >
          <Banknote className="w-8 h-8" />
          <span className="font-bold text-xl tracking-wide">CONTANTI</span>
        </button>
        <button
          data-testid={`${testidPrefix}-pay-card-big`}
          disabled={disabled || busy}
          onClick={() => pay("card")}
          className="h-24 rounded-lg bg-[#1E2D5B] hover:bg-[#162244] text-white flex flex-col items-center justify-center gap-1.5 shadow-md btn-tactile disabled:opacity-40"
        >
          <CreditCard className="w-8 h-8" />
          <span className="font-bold text-xl tracking-wide">CARTA</span>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid={`${testidPrefix}-pay-other`}
          disabled={disabled || busy}
          onClick={() => setOpen(!open)}
          className="h-12 rounded-md bg-white border-2 border-dashed border-velta-border text-velta-mute hover:text-velta-ink hover:border-velta-ink text-sm font-medium flex items-center justify-center gap-1.5 btn-tactile disabled:opacity-40"
        >
          Altri pagamenti <ChevronDown className={`w-4 h-4 transition ${open ? "rotate-180" : ""}`} />
        </button>
        {children}
      </div>
      {open && (
        <div className="bg-white border border-velta-border rounded-md shadow-xl p-1 grid grid-cols-2 gap-1" data-testid={`${testidPrefix}-pay-other-menu`}>
          <button
            data-testid={`${testidPrefix}-pay-other-multi`}
            onClick={() => { setOpen(false); setShowMulti(true); }}
            className="col-span-2 h-11 rounded text-sm font-semibold bg-[#1E2D5B]/5 hover:bg-[#1E2D5B]/10 text-[#1E2D5B] text-left px-3 flex items-center gap-2"
          >
            <Layers className="w-4 h-4" /> Pagamento multiplo (dividi il totale)
          </button>
          {(Array.isArray(singles) ? singles : []).map(({ id, label }) => {
            const Icon = METHOD_ICONS[id];
            return (
              <button
                key={id}
                data-testid={`${testidPrefix}-pay-other-${id}`}
                onClick={() => pay(id)}
                className="h-11 rounded text-sm hover:bg-velta-bg text-left px-3 flex items-center gap-2"
              >
                <Icon className="w-4 h-4 text-velta-mute" /> {label}
              </button>
            );
          })}
          <button
            data-testid={`${testidPrefix}-pay-other-bonifico`}
            onClick={() => pay("bonifico")}
            className="h-11 rounded text-sm hover:bg-velta-bg text-left px-3 flex items-center gap-2"
          >
            <Landmark className="w-4 h-4 text-velta-mute" /> Bonifico
          </button>
          {(Array.isArray(customMethods) ? customMethods : []).map((m) => (
            <button
              key={m.id}
              data-testid={`${testidPrefix}-pay-other-${m.id}`}
              onClick={() => pay(m.id)}
              className="h-11 rounded text-sm hover:bg-velta-bg text-left px-3 flex items-center gap-2 text-[#7C3AED]"
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      {showMulti && (
        <MultiPayModal
          total={total}
          testidPrefix={testidPrefix}
          onClose={() => setShowMulti(false)}
          onConfirm={(method, breakdown, opts) => { setShowMulti(false); pay(method, breakdown, opts); }}
        />
      )}
    </div>
  );
}
